import { Form } from "@/components/elements/Form";
import { useGetTalentSchema } from "@/features/talent/hooks/useGetTalentSchema";
import { TalentTitle } from "@/features/talent/components/talent-common/TalentTitle";
import { useLocationInfo } from "@/hooks/useLocationInfo";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Stack } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { IconSave } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AppRoutes } from "@/routes/config";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { TalentFormWorkExperience } from "@/features/talent/components/talent-form/TalentFormWorkExperience";
import { TalentFormResidenceStatus } from "@/features/talent/components/talent-form/TalentFormResidenceStatus";
import { TalentFormJobPreferences } from "@/features/talent/components/talent-form/TalentFormJobPreferences";
import { TalentFormInternalMemo } from "@/features/talent/components/talent-form/TalentFormInternalMemo";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { convertTalentFormDataReq, isDefaultValuesChanged, isFormDataEmpty, isValueDifferent } from "@/features/talent/utils/convertTalentFormData";
import { createTalentApi, updateTalentApi, fetchTalentExistsApi, fetchCheckPassportDuplicateApi } from "@/api/services/main/talent";
import { TGetTalentExistsResDto, TGetTalentExistsDto } from "@/api/services/main/talent/dtos/get-talent-exists.dto";
import { TCheckPassportDuplicateResDto, TCheckPassportDuplicateDto } from "@/api/services/main/talent/dtos/check-passport-duplicate.dto";
import { TCreateTalentReqDto } from "@/api/services/main/talent/dtos/create-talent-req.dto";
import { TUpdateTalentReqDto } from "@/api/services/main/talent/dtos/update-talent-req.dto";
import { useGetLanguageTestList, useGetSpecificSkillCertificateList } from "@/api/services/global/master";
import { FCC } from "@/types/common";
import { TalentFormLanguageAbility } from "@/features/talent/components/talent-form/TalentFormLanguageAbility";
import { TalentFormCertificateFiles } from "@/features/talent/components/talent-form/TalentFormCertificateFiles";
import { TalentFormBasicInfo } from "@/features/talent/components/talent-form/TalentFormBasicInfo";
import { TalentFormEducationBackground } from "@/features/talent/components/talent-form/TalentFormEducationBackground";
import { TalentFormRecruitmentStatus } from "@/features/talent/components/talent-form/TalentFormRecruitmentStatus";
import { TalentFormMemo } from "@/features/talent/components/talent-form/TalentFormMemo";
import { TalentFormInterviewEvaluation } from "@/features/talent/components/talent-form/TalentInterviewEvaluation";
import { TalentFormFamilyInfo } from "@/features/talent/components/talent-form/TalentFormFamilyInfo";
import { TalentFormPartnerInfo } from "@/features/talent/components/talent-form/TalentFormPartnerInfo";
import { TalentProfileHeader } from "@/features/talent/components/talent-detail/TalentProfileHeader";
import { getDynamicRoute } from "@/utils/url";
import { TalentFormComment } from "@/features/talent/components/talent-form/TalentFormComment";
import { queryClient } from "@/constants/queryClient";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { TMasterLanguageTest } from "@/api/entities/master-language-test.entity";
import { useDialog } from "@/hooks/useDialog";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { DuplicateTalentModal } from "@/features/talent/components/talent-common/DuplicateTalentModal";
import { DuplicatePassportModal } from "@/features/talent/components/talent-common/DuplicatePassportModal";
import { Typography } from "@mui/material";
import { TALENT_WIDTH_FULL, TALENT_WIDTH_LEFT_COLUMN, TALENT_WIDTH_RIGHT_COLUMN } from "@/features/talent/constants/width";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { InputRuleBanner } from "@/components/elements/InputRuleBanner";
import { useGetMatchInfo } from "@/api/services/main/match";
import { scrollToFirstError } from "@/utils/scrollToFirstError";

type TTalentFormProps = {
  defaultValues?: Partial<TTalentForm>;
};

export const TalentForm: FCC<TTalentFormProps> = ({ defaultValues }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { talentSchema } = useGetTalentSchema();

  const { languageTestLevelOptions, data: languageTestData } = useGetLanguageTestList();
  const { data: specificSkillCertificateData } = useGetSpecificSkillCertificateList();

  const { talentId } = useParams();
  const { data: matchData } = useGetMatchInfo();

  const validationContextRef = useRef({ isFormSubmitted: false });
  const careIndustryValidationRef = useRef<(() => boolean) | null>(null);
  const pendingSubmissionRef = useRef<TTalentForm | null>(null);
  const backModal = useDialog();
  const duplicateTalentModal = useDialog();
  const duplicatePassportModal = useDialog();
  const [blockerEnabled, setBlockerEnabled] = useState(true);
  const [redirect, setRedirect] = useState<{ route: string } | null>(null);
  const [duplicateTalents, setDuplicateTalents] = useState<TGetTalentExistsDto[]>([]);
  const [duplicatePassports, setDuplicatePassports] = useState<TCheckPassportDuplicateDto[]>([]);
  const [pendingSaveData, setPendingSaveData] = useState<TTalentForm | null>(null);
  const [pendingPassportNumber, setPendingPassportNumber] = useState<string>("");
  const [isChangeValue, setIsChangeValue] = useState(false);

  const methods = useForm<TTalentForm>({
    defaultValues: {
      workExperiences: [
        {
          jobType: "",
          companyName: "",
          countryId: null,
          startDate: null,
          endDate: null,
        },
      ],
      contactMethods: [{ contactType: null, contactInfo: null }],
      ...defaultValues,
    },
    resolver: yupResolver(talentSchema),
    context: validationContextRef.current,
    mode: "all",
    reValidateMode: "onBlur",
  });

  const watchValue = methods.watch();

  // Prefill template fields from match data when creating a new talent
  useEffect(() => {
    if (talentId || !matchData?.data) return;
    const { talentCharacteristicMemoTemplate, talentInterviewEvaluationTemplate } = matchData.data;
    if (talentCharacteristicMemoTemplate && !methods.getValues("memo")) {
      methods.setValue("memo", talentCharacteristicMemoTemplate);
    }
    if (talentInterviewEvaluationTemplate && !methods.getValues("interviewEvaluation")) {
      methods.setValue("interviewEvaluation", talentInterviewEvaluationTemplate);
    }
  }, [talentId, matchData, methods]);

  const recruitmentStatus = useWatch({ control: methods.control, name: "recruitmentStatus" });
  const prevRecruitmentStatusRef = useRef(recruitmentStatus);
  useEffect(() => {
    if (prevRecruitmentStatusRef.current !== recruitmentStatus) {
      prevRecruitmentStatusRef.current = recruitmentStatus;
      const conditionalFields: (keyof TTalentForm)[] = [
        "fullName",
        "matchOfficeId",
        "nationalityId",
        "gender",
        "location",
        "companySpecificIndustryId",
      ];
      methods.trigger(conditionalFields);
    }
  }, [recruitmentStatus, methods]);

  const isFormInitializedRef = useRef(false);
  const initialValuesRef = useRef<Partial<TTalentForm> | null>(null);
  const defaultValuesRef = useRef<Partial<TTalentForm> | undefined>(defaultValues);
  const isNavigatingRef = useRef(false);

  useEffect(() => {
    if (!isFormInitializedRef.current) {
      return;
    }

    if (isNavigatingRef.current) {
      return;
    }

    if (!defaultValues) {
      const isEmpty = isFormDataEmpty(watchValue as Partial<TTalentForm>);
      setIsChangeValue(!isEmpty);
    } else {
      if (!initialValuesRef.current) {
        setIsChangeValue(false);
        return;
      }
      const hasChanged = Object.keys(initialValuesRef.current).some((key) => {
        const currentValue = watchValue[key as keyof TTalentForm];
        const initialValue = initialValuesRef.current![key as keyof TTalentForm];
        return isValueDifferent(currentValue, initialValue);
      });
      setIsChangeValue(hasChanged);
    }
  }, [watchValue, defaultValues]);

  useEffect(() => {
    if (isNavigatingRef.current) {
      return;
    }

    if (isDefaultValuesChanged(defaultValuesRef.current, defaultValues)) {
      defaultValuesRef.current = defaultValues;
      if (defaultValues) {
        methods.reset(defaultValues);
        setTimeout(() => {
          const currentValues = methods.getValues();
          initialValuesRef.current = { ...currentValues };
          isFormInitializedRef.current = true;
        }, 0);
      } else {
        isFormInitializedRef.current = true;
        initialValuesRef.current = null;
      }
    } else if (!isFormInitializedRef.current) {
      if (defaultValues) {
        methods.reset(defaultValues);
        setTimeout(() => {
          const currentValues = methods.getValues();
          initialValuesRef.current = { ...currentValues };
          isFormInitializedRef.current = true;
        }, 0);
      } else {
        isFormInitializedRef.current = true;
        initialValuesRef.current = null;
      }
    }
  }, [defaultValues, methods]);

  useLocationInfo({ methods, names: { zipCode: "zipCode", province: "prefecture", municipality: "city", address: "address" } });

  useEffect(() => {
    if (isNavigatingRef.current) {
      const timer = setTimeout(() => {
        isNavigatingRef.current = false;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  const onSubmit = async (data: TTalentForm) => {
    // Check validation first
    if (careIndustryValidationRef.current && !careIndustryValidationRef.current()) {
      pendingSubmissionRef.current = data;
      return;
    }
    await handleSaveData(data);
  };

  const handleContinueSubmit = async () => {
    if (pendingSubmissionRef.current) {
      await handleSaveData(pendingSubmissionRef.current);
      pendingSubmissionRef.current = null;
    }
  };

  const performSave = async (payload: TCreateTalentReqDto | TUpdateTalentReqDto) => {
    if (talentId) {
      await updateTalentApi(talentId, payload);
      toast.success(API_SUCCESS_MESSAGE);

      await queryClient.refetchQueries({
        queryKey: [QUERY_KEYS.TALENT.FETCH_DETAIL, talentId],
      });

      setBlockerEnabled(false);

      const detailRoute = getDynamicRoute(AppRoutes.talentDetail, { talentId: talentId });
      setRedirect({ route: detailRoute });
    } else {
      await createTalentApi(payload);
      toast.success(API_SUCCESS_MESSAGE);
      resetFormState();
      setBlockerEnabled(false);
      setRedirect({ route: AppRoutes.talentList });
    }
  };

  useEffect(() => {
    if (!blockerEnabled && redirect) {
      navigate(redirect.route);
      setRedirect(null);
    }
  }, [blockerEnabled, redirect, navigate]);

  const handleSaveData = async (data: TTalentForm) => {
    // Submitting before the certificate master list has loaded would drop every
    // certificate from the payload.
    if (!specificSkillCertificateData?.length) return;
    try {
      const payload = convertTalentFormDataReq(data, languageTestData as TMasterLanguageTest[], specificSkillCertificateData);

      // Check passport duplicate first
      if (payload.passportNumber) {
        const passportDuplicate: TCheckPassportDuplicateResDto = await fetchCheckPassportDuplicateApi(
          talentId ? Number(talentId) : undefined,
          payload.passportNumber,
        );

        // Check if there are duplicate passports
        if (passportDuplicate?.data?.length > 0) {
          const dataPassportDuplicate = passportDuplicate.data;

          if (dataPassportDuplicate.length > 0) {
            setDuplicatePassports(dataPassportDuplicate);
            setPendingPassportNumber(payload.passportNumber);
            setPendingSaveData(data);
            duplicatePassportModal.show();
            return;
          }
        }
      }

      // Check talent exists (name and birthday)
      const talentExists: TGetTalentExistsResDto = await fetchTalentExistsApi(Number(talentId), payload.fullName, payload.birthday);

      // Check if there are duplicate talents
      if (talentExists?.data?.length > 0) {
        const dataTalentExists = talentExists.data;

        if (dataTalentExists.length > 0) {
          setDuplicateTalents(dataTalentExists);
          setPendingSaveData(data);
          duplicateTalentModal.show();
          return;
        }
      }

      // Proceed with save if no duplicates found
      await performSave(payload);
    } catch (e) {
      showError(e);
    }
  };

  // Function to receive validation function from TalentFormLanguageAbility
  const handleValidationCheck = (validateFn: () => boolean) => {
    careIndustryValidationRef.current = validateFn;
  };

  // Navigation helpers
  const getBackRoute = () => {
    return talentId ? getDynamicRoute(AppRoutes.talentDetail, { talentId }) : AppRoutes.talentList;
  };

  const navigateBack = () => {
    navigate(getBackRoute());
  };

  const resetFormState = () => {
    validationContextRef.current.isFormSubmitted = false;
    if (defaultValues) {
      methods.reset(defaultValues);
    } else {
      methods.reset();
    }
    setIsChangeValue(false);
    isFormInitializedRef.current = false;
  };

  // Back button handlers
  const handleBackClick = () => {
    if (isChangeValue) {
      setBlockerEnabled(false);
      backModal.show();
    } else {
      navigateBack();
    }
  };

  const handleConfirmBack = () => {
    isNavigatingRef.current = true;
    resetFormState();
    setBlockerEnabled(false);
    navigateBack();
  };

  const handleCancelBack = () => {
    setBlockerEnabled(true);
    backModal.cancel();
  };

  const handleFileChange = () => {
    setIsChangeValue(true);
  };

  const handleDuplicateModalCancel = () => {
    duplicateTalentModal.cancel();
    setPendingSaveData(null);
  };

  const handleDuplicateModalSave = async () => {
    if (pendingSaveData) {
      const payload = convertTalentFormDataReq(pendingSaveData, languageTestData as TMasterLanguageTest[], specificSkillCertificateData);
      await performSave(payload);
      duplicateTalentModal.cancel();
      setPendingSaveData(null);
    }
  };

  const handleDuplicatePassportModalCancel = () => {
    duplicatePassportModal.cancel();
    setPendingSaveData(null);
    setPendingPassportNumber("");
  };

  // Ref to the certificateFiles area — used as the scroll anchor for errors on the unregistered
  // `relatedFileTypes` / `displayFiles` fields (whose yup errors carry no usable DOM ref).
  const certificateFilesRef = useRef<HTMLDivElement>(null);

  // Scroll to first errored field (top-to-bottom DOM order) on every submit attempt.
  // Use submitCount (primitive, increments per attempt) instead of the errors object reference,
  // because RHF's formState.errors Proxy often keeps the same reference across submits — a
  // useEffect with `errors` in deps would not re-fire reliably.
  const submitCount = methods.formState.submitCount;
  useEffect(() => {
    if (!submitCount) return;
    setTimeout(() => {
      scrollToFirstError(methods.formState.errors, {
        customAnchors: {
          relatedFileTypes: certificateFilesRef.current,
          displayFiles: certificateFilesRef.current,
        },
      });
    }, 100);
  }, [submitCount, methods]);

  return (
    <>
      <Stack mt={1}>
        {talentId ? <TalentProfileHeader defaultValues={defaultValues} /> : <TalentTitle />}
        <Stack mt={1}>
          <Form
            methods={methods}
            onSubmit={onSubmit}
            schema={talentSchema}
            shouldBlock={({ currentLocation, nextLocation }) => {
              return isChangeValue && currentLocation.pathname !== nextLocation.pathname;
            }}
            enableBlocker={blockerEnabled}
            onBeforeSubmit={() => {
              validationContextRef.current.isFormSubmitted = true;
            }}
          >
            <Box width="100%">
              <InputRuleBanner rule={matchData?.data?.talentInputRule} />
              <Grid container spacing={2}>
                {/* Left Column */}
                <Grid item xs={12} md={8}>
                  <TalentFormRecruitmentStatus personInChargeId={defaultValues?.personInChargeId} matchOfficeId={defaultValues?.matchOfficeId} />
                  <TalentFormBasicInfo />
                  <TalentFormResidenceStatus />
                  <TalentFormLanguageAbility onValidationCheck={handleValidationCheck} onContinueSubmit={handleContinueSubmit} />
                  <TalentFormEducationBackground />
                  <TalentFormWorkExperience />
                  <Box ref={certificateFilesRef}>
                    <TalentFormCertificateFiles onFileChange={handleFileChange} isEditMode={!!talentId} />
                  </Box>
                  <TalentFormMemo />
                  <TalentFormInterviewEvaluation />
                  <TalentFormFamilyInfo />
                </Grid>

                {/* Right Column */}
                <Grid item xs={12} md={4}>
                  <TalentFormJobPreferences />
                  <TalentFormInternalMemo />
                  {talentId && <TalentFormComment />}
                  <TalentFormPartnerInfo />
                </Grid>
              </Grid>

              <FooterContainer>
                <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
                  <Button variant="ghost" size="sm" onClick={handleBackClick} sx={{ width: "60px" }}>
                    戻る
                  </Button>
                  <Stack direction="row" gap={3}>
                    <Button type="submit" variant="primary" size="sm" startIcon={<IconSave />} sx={{ fontWeight: 500, width: "88px" }}>
                      保存
                    </Button>
                  </Stack>
                </Stack>
              </FooterContainer>
            </Box>
          </Form>
        </Stack>
      </Stack>
      <ConfirmSaveModal open={backModal.open} onOk={handleConfirmBack} onClose={handleCancelBack}>
        <Typography>入力が完了していません。このまま戻ってもよろしいですか？</Typography>
      </ConfirmSaveModal>
      <DuplicateTalentModal
        open={duplicateTalentModal.open}
        onClose={handleDuplicateModalCancel}
        onSave={handleDuplicateModalSave}
        duplicateTalents={duplicateTalents}
      />
      <DuplicatePassportModal
        open={duplicatePassportModal.open}
        onClose={handleDuplicatePassportModalCancel}
        duplicateTalents={duplicatePassports}
        passportNumber={pendingPassportNumber}
      />
    </>
  );
};
